import { CSSProperties, useCallback } from 'react';
import styled from 'styled-components6';
import { DEFAULT_MAP_ID, useMap } from './hooks';

const MapContainer = styled.div`
    position: relative;
    height: 100%;
    flex: 1;
    width: 100%;

    iframe {
        width: 100%;
        height: 100%;
    }

    opacity: 0;

    transition: opacity 0.25s ease-in-out;
`;

interface MapProps {
    loader?: JSX.Element | null;
    style?: CSSProperties;
    initialConfig?: Partial<google.maps.MapOptions>;
    children?: React.ReactNode;
}

const defaultMapOptions: Partial<google.maps.MapOptions> = {
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    maxZoom: 15,
    minZoom: 5,
    zoom: 5, // this covers the entire US
    clickableIcons: false,
    mapId: DEFAULT_MAP_ID,
    center: {
        // center of US
        lat: 39.8283,
        lng: -98.5795,
    },
};

export const GoogleMap = ({
    loader = null,
    ...props
}: MapProps): JSX.Element | null => {
    const { apiKey } = useMap();

    if (!apiKey) {
        return loader;
    }

    return (
        <GoogleMapRenderer loader={loader} {...props}>
            {props.children}
        </GoogleMapRenderer>
    );
};

const GoogleMapRenderer = ({
    initialConfig = {},
    children,
    style,
    loader = null,
}: MapProps): JSX.Element | null => {
    const { map, setMap, isGMScriptLoaded: isReady } = useMap();

    const containerLoaded = useCallback(
        (node: HTMLElement | null) => {
            if (!node || map) {
                return;
            }

            const newMap = new google.maps.Map(node, defaultMapOptions);
            newMap.setOptions({ ...defaultMapOptions, ...initialConfig });
            newMap.addListener('tilesloaded', () => {
                node.style.opacity = '1';
            });

            setMap(newMap);
        },
        [map]
    );

    if (!isReady) {
        return loader;
    }

    return (
        <MapContainer
            ref={(ref: HTMLDivElement) => containerLoaded(ref)}
            style={style}
            id="rwl-map-container"
        >
            {children}
        </MapContainer>
    );
};
